import { z } from 'zod';

const iso = z.string();
const nullableStr = z.string().nullable();

export const studioSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  status: z.enum(['preview', 'live', 'archived']),
  isPreview: z.boolean(),
  timezone: z.string(),
  currency: z.string(),
  today: z.string(),
  settings: z.object({
    name: z.string(),
    shortName: z.string(),
    tagline: z.string(),
    description: z.string(),
    phone: z.string(),
    address: z.string(),
    city: z.string(),
    mapUrl: z.string(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    accentColor: z.string(),
    heroImagePath: nullableStr,
    logoPath: nullableStr,
    slotStepMin: z.number(),
    bookingHorizonDays: z.number(),
    minLeadMin: z.number(),
    cancelCutoffHours: z.number(),
    reminderHoursBefore: z.number(),
    bookingRules: z.string(),
  }),
  infoCards: z.array(z.object({ position: z.number(), title: z.string(), body: z.string(), icon: z.string() })),
  services: z.array(
    z.object({
      id: z.uuid(),
      key: z.string(),
      name: z.string(),
      category: z.string(),
      description: z.string(),
      durationMin: z.number(),
      bufferMin: z.number(),
      priceCents: z.number(),
      priceIsFrom: z.boolean(),
    }),
  ),
  gallery: z.array(z.object({ id: z.uuid(), imagePath: z.string(), caption: z.string() })),
  workingHours: z.array(z.object({ weekday: z.number(), opens: z.string(), closes: z.string() })),
  exceptions: z.array(
    z.object({ day: z.string(), isClosed: z.boolean(), opens: nullableStr, closes: nullableStr, note: z.string() }),
  ),
});
export type Studio = z.infer<typeof studioSchema>;
export type StudioService = Studio['services'][number];

export const availabilitySchema = z.object({
  serviceId: z.uuid(),
  timezone: z.string(),
  from: z.string(),
  to: z.string(),
  horizonEnd: z.string(),
  days: z.array(
    z.object({
      date: z.string(),
      isOpen: z.boolean(),
      slots: z.array(z.object({ startsAt: iso, available: z.boolean() })),
    }),
  ),
});
export type Availability = z.infer<typeof availabilitySchema>;

const bookingBase = z.object({
  id: z.uuid(),
  code: z.string(),
  status: z.enum(['confirmed', 'arrived', 'ready', 'completed', 'cancelled', 'no_show']),
  startsAt: iso,
  endsAt: iso,
  occupiedUntil: iso,
  serviceId: z.uuid(),
  serviceName: z.string(),
  durationMin: z.number(),
  bufferMin: z.number(),
  priceCents: z.number(),
  priceIsFrom: z.boolean(),
  currency: z.string(),
  resourceId: z.uuid(),
  resourceName: z.string().nullable(),
  isDemo: z.boolean(),
  version: z.number(),
  createdAt: iso,
  cancelledAt: iso.nullable(),
  cancelledBy: z.enum(['client', 'owner']).nullable(),
  customerName: z.string(),
  carInfo: z.string(),
});
export type BookingStatus = z.infer<typeof bookingBase>['status'];

export const createdBookingSchema = bookingBase.extend({ replayed: z.boolean() });

export const clientBookingSchema = bookingBase.extend({
  canCancel: z.boolean(),
  cancelCutoffHours: z.number(),
  reminderHoursBefore: z.number(),
  pushSubscribed: z.boolean(),
  reminder: z.object({ status: z.string(), runAt: iso }).nullable(),
});
export type ClientBooking = z.infer<typeof clientBookingSchema>;

export const paymentSummarySchema = z.object({
  paidCents: z.number(),
  refundedCents: z.number(),
  netCents: z.number(),
});

export const ownerBookingSchema = bookingBase.extend({
  customerPhone: z.string(),
  comment: z.string(),
  source: z.enum(['client', 'owner']),
  arrivedAt: iso.nullable(),
  readyAt: iso.nullable(),
  completedAt: iso.nullable(),
  cancelReason: z.string(),
  payments: paymentSummarySchema.optional(),
});
export type OwnerBooking = z.infer<typeof ownerBookingSchema>;

export const ownerBookingDetailSchema = ownerBookingSchema.extend({
  payments: paymentSummarySchema.extend({
    items: z.array(
      z.object({ id: z.uuid(), kind: z.enum(['payment', 'refund']), amountCents: z.number(), method: z.string(), receivedAt: iso, note: z.string() }),
    ),
  }),
  events: z.array(z.object({ type: z.string(), actor: z.string(), at: iso, payload: z.record(z.string(), z.unknown()) })),
  pushSubscribed: z.boolean(),
});
export type OwnerBookingDetail = z.infer<typeof ownerBookingDetailSchema>;

export const myStudiosSchema = z.array(
  z.object({
    tenantId: z.uuid(),
    slug: z.string(),
    status: z.string(),
    timezone: z.string(),
    currency: z.string(),
    role: z.string(),
    name: z.string(),
    accentColor: z.string(),
  }),
);

export const ownerStudioSchema = z.object({
  tenantId: z.uuid(),
  slug: z.string(),
  status: z.string(),
  timezone: z.string(),
  currency: z.string(),
  today: z.string(),
  activationProblems: z.array(z.string()),
  settings: studioSchema.shape.settings.extend({ ownerModifiedFields: z.array(z.string()) }),
  infoCards: studioSchema.shape.infoCards,
  services: z.array(
    studioSchema.shape.services.element.extend({
      isActive: z.boolean(),
      position: z.number(),
      origin: z.string(),
      resourceIds: z.array(z.uuid()),
    }),
  ),
  resources: z.array(
    z.object({ id: z.uuid(), key: z.string(), name: z.string(), kind: z.string(), isActive: z.boolean(), position: z.number() }),
  ),
  workingHours: studioSchema.shape.workingHours,
  exceptions: studioSchema.shape.exceptions,
  gallery: z.array(
    z.object({ id: z.uuid(), imagePath: z.string(), caption: z.string(), origin: z.string(), position: z.number() }),
  ),
});
export type OwnerStudio = z.infer<typeof ownerStudioSchema>;

export const statsSchema = z.object({
  period: z.object({ from: z.string(), to: z.string(), timezone: z.string(), startsAt: iso, endsAt: iso }),
  arrivals: z.number(),
  completed: z.number(),
  completedValueCents: z.number(),
  created: z.number(),
  cancelled: z.number(),
  noShows: z.number(),
  scheduled: z.object({ count: z.number(), valueCents: z.number() }),
  payments: z.object({ receivedCents: z.number(), refundedCents: z.number(), netCents: z.number(), count: z.number() }),
});
export type Stats = z.infer<typeof statsSchema>;

export const blockSchema = z.object({
  id: z.uuid(),
  resourceId: z.uuid(),
  resourceName: z.string(),
  startsAt: iso,
  endsAt: iso,
  reason: z.string(),
});
export type Block = z.infer<typeof blockSchema>;

export const agendaSchema = z.object({
  day: z.string(),
  timezone: z.string(),
  exception: z
    .object({ isClosed: z.boolean(), opens: nullableStr, closes: nullableStr, note: z.string() })
    .nullable(),
  hours: z.array(z.object({ opens: z.string(), closes: z.string() })),
  bookings: z.array(ownerBookingSchema),
  blocks: z.array(blockSchema),
});
export type Agenda = z.infer<typeof agendaSchema>;

export const idSchema = z.object({ id: z.uuid() }).loose();
